// src/frontend/components/room/FileList.jsx
import React, {useEffect, useState} from 'react';
import {useRoom} from '../../context/RoomContext';
import {socket} from '../../lib/socket';
import {SOCKET_EVENTS} from "../../constants/events.js";
import toast from "react-hot-toast";

export default function FileList() {
    const {roomId} = useRoom();
    const [files, setFiles] = useState([]); // Array von {name, path, id, uploadedBy, timestamp}
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (!roomId) return;

        // Initiale komplette Liste beim Join
        socket.on(SOCKET_EVENTS.UPDATE_FILES, (fileList) => {
            console.log('[FILES] ' + SOCKET_EVENTS.UPDATE_FILES + ' (komplette Liste):', fileList);
            const normalized = Array.isArray(fileList) ? [...fileList] : [];
            setFiles(normalized);
        });

        // Neue einzelne Datei nach Upload
        socket.on(SOCKET_EVENTS.FILE_UPLOADED, (newFile) => {
            console.log('[FILES] ' + SOCKET_EVENTS.FILE_UPLOADED + ' (einzelne Datei):', newFile);

            // newFile ist ein einzelnes Objekt → wir machen daraus ein Array
            const addedFile = newFile ? [newFile] : [];

            setFiles((prev) => {
                // Duplikate vermeiden
                const existingIds = new Set(prev.map(f => f.id));
                const uniqueNew = addedFile.filter(f => f?.id && !existingIds.has(f.id));
                return [...prev, ...uniqueNew];
            });
        });

        socket.on(SOCKET_EVENTS.FILE_DELETED, (deletedFileIdOrName) => {
            console.log('[FILES] ' + SOCKET_EVENTS.FILE_DELETED + ' empfangen:', deletedFileIdOrName);

            setFiles((prev) => {
                // Entferne die Datei anhand von id oder name (Backend sendet wahrscheinlich filename als String)
                const filtered = prev.filter(file =>
                    file.id !== deletedFileIdOrName &&
                    file.name !== deletedFileIdOrName
                );
                console.log('[FILES] Nach Delete gefiltert:', filtered.length, 'Files übrig');
                return filtered;
            });
        });

        // Initiale Anfrage (falls nötig)
        socket.emit('getFiles', {roomId});

        return () => {
            socket.off(SOCKET_EVENTS.UPDATE_FILES);
            socket.off(SOCKET_EVENTS.FILE_UPLOADED);
            socket.off(SOCKET_EVENTS.FILE_DELETED);
        };
    }, [roomId]);

    useEffect(() => {
        console.log('[FILES] UI updatet mit Files:', files, 'Länge:', files.length);
    }, [files]);

    const handleUpload = async (uploadedFiles) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('roomId', roomId);

        // Korrekt: 'files' (Plural) für upload.array("files")
        for (const file of uploadedFiles) {
            formData.append('files', file);
        }

        try {
            toast.loading('Lade hoch...', {id: 'upload-toast'});
            const response = await fetch('/api/files', {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();
            console.log('[FILES] Upload-Response:', data);
            if (response.ok) {
                console.log('[FILES] Upload OK – warte auf ' + SOCKET_EVENTS.FILE_UPLOADED);
                toast.success('Datei hochgeladen!', {id: 'upload-toast'});
            } else {
                toast.error('Upload fehlgeschlagen', {id: 'upload-toast'});
            }
        } catch (error) {
            console.error('[FILES] Upload error:', error);
            toast.error('Upload fehlgeschlagen', {id: 'upload-toast'});
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (fileId) => {
        console.log('[FILES] Delete angefragt für ID:', fileId);

        try {
            const response = await fetch(`/api/files/${fileId}?roomId=${roomId}`, {
                method: 'DELETE',
            });
            const data = await response.json();
            console.log('[FILES] Delete-Response:', data);
            if (response.ok) {
                toast.success('Datei gelöscht');
                // Optional: Optimistisch entfernen, bis Server bestätigt
                setFiles((prev) => prev.filter(f => f.id !== fileId));
            } else {
                toast.error('Löschen fehlgeschlagen');
            }
        } catch (error) {
            console.error('[FILES] Delete error:', error);
            toast.error('Fehler beim Löschen');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        handleUpload(droppedFiles);
    };

    const handleDragOver = (e) => e.preventDefault();

    return (
        <div
            className="bg-white/80 dark:bg-white/5 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 text-center min-h-[400px]"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
        >
            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">Dateien</h3>
            {uploading && <p className="text-blue-500 animate-pulse mb-4">Lade hoch...</p>}

            <input
                type="file"
                multiple
                onChange={(e) => handleUpload(Array.from(e.target.files))}
                className="mb-6 p-3 border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-xl cursor-pointer w-full text-gray-700 dark:text-gray-300"
            />

            <ul className="space-y-4 mt-6">
                {files.map((file) => (
                    <li
                        key={file.id}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-gray-100 dark:bg-gray-800 p-4 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                        <div className="flex-1 mb-2 sm:mb-0">
                            <a
                                href={file.path}
                                download={file.name}
                                className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                                {file.name}
                            </a>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Hochgeladen von {file.uploadedBy} • {file.timestamp}
                            </p>
                        </div>
                        <button
                            onClick={() => handleDelete(file.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium px-4 py-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        >
                            Löschen
                        </button>
                    </li>
                ))}
            </ul>

            {files.length === 0 && (
                <p className="text-gray-500 dark:text-gray-400 mt-8 italic">
                    Noch keine Dateien – Drag & Drop oder auswählen!
                </p>
            )}
        </div>
    );
}